import ReactDom from 'react-dom';
import { Icon as BlueIcon } from '@blueprintjs/core';
import { useContext, useMemo } from 'react';
import { useQuery } from 'react-query';
import { getProduct } from '@services/queries/products';
import { ProductStatus } from '@models/Product.model';
import { ProductSpecsBody } from '@pages/Asset/components/Sidebar/components/Data/components/ProductSpec';
import DefaultProductImage from '@assets/png/product.png';
import { ReactComponent as RefreshFilledIcon } from '@assets/svg/refresh-filled-red.svg';
import { ReactComponent as DeleteFilledIcon } from '@assets/svg/delete-filled.svg';

import {
  Container,
  Overlay,
  Header,
  Title,
  Row,
  Grid,
  InfoContainer,
  Label,
  Text,
  EmptyText,
  ProductImage,
  LinkText,
  LifeCycleWrapper,
  LifeCycleText,
  Bottom,
  DescriptionSection,
  DescriptionText,
  EmptyDescriptionText,
  ProductSpecContainer,
  SearchSection,
  EmptyProductDetails,
} from './ProductMatchDetailsDialog.styles';
import { OnboardingFileContext } from '../../OnboardingFile.query';
import { ProductMatchDetailsLoading } from './ProductMatchDetails.loading';

interface IInfoContainerComponent {
  label: string;
  field?: string | null;
}
const InfoContainerComponent = ({ label, field }: IInfoContainerComponent) => {
  return (
    <InfoContainer>
      <Label>{label}</Label>
      {field && field !== null ? (
        <Text>{field}</Text>
      ) : (
        <EmptyText>Empty</EmptyText>
      )}
    </InfoContainer>
  );
};

const ProductLifeCycleComponent = ({ status }: { status?: ProductStatus }) => {
  const iconType = useMemo((): any => {
    switch (status) {
      case 'supported':
      case 'presumed_active':
        return 'tick-circle';
      default:
        return null;
    }
  }, [status]);

  const iconColor = useMemo((): any => {
    switch (status) {
      case 'supported':
        return '#52BD94';
      case 'presumed_active':
        return '#8F95B2';
      default:
        return '#D14343';
    }
  }, [status]);

  const statusText = useMemo((): any => {
    switch (status) {
      case 'supported':
        return 'Supported (Presumed)';
      case 'discontinued':
        return 'End of Life (Discontinued)';
      case 'presumed_active':
        return 'Supported (Presumed)';
      case 'replaced':
        return 'End of Life (Discontinued) & Replaced';
      default:
        return 'Unknown';
    }
  }, [status]);
  return (
    <LifeCycleWrapper>
      {!iconType && <BlueIcon icon={iconType} color={iconColor} />}
      {status === 'replaced' && <RefreshFilledIcon />}
      {status === 'discontinued' && <DeleteFilledIcon />}

      <LifeCycleText>{statusText}</LifeCycleText>
    </LifeCycleWrapper>
  );
};

interface IProductMatchDetailsDialog {
  open: boolean;
  onClose: () => void;
}

export const ProductMatchDetailsDialog = ({
  open,
  onClose,
}: IProductMatchDetailsDialog) => {
  const portalDiv = document.getElementById('portal');
  const { selectedProductId } = useContext(OnboardingFileContext);

  const {
    data: matchedData,
    isLoading,
    isError,
  } = useQuery(['productDetails', selectedProductId.value], async () => {
    return getProduct(selectedProductId.value);
  });

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <Title>Product Details</Title>
              <BlueIcon icon="cross" color="#8F95B2" onClick={onClose} />
            </Header>
            {isLoading && <ProductMatchDetailsLoading />}

            {matchedData && (
              <>
                <Row>
                  <ProductImage
                    src={matchedData?.imageUrl || DefaultProductImage}
                    brokenSrc={DefaultProductImage}
                  />
                  <Grid>
                    <InfoContainer>
                      <InfoContainerComponent
                        label="Product Name"
                        field={matchedData?.name}
                      />
                    </InfoContainer>
                    <InfoContainer>
                      <InfoContainerComponent
                        label="Manufacturer"
                        field={matchedData?.manufacturer.name}
                      />
                    </InfoContainer>
                    <InfoContainer>
                      <InfoContainerComponent
                        label="Category"
                        field={matchedData?.category?.name}
                      />
                    </InfoContainer>
                    <InfoContainer>
                      <InfoContainerComponent
                        label="Model #"
                        field={matchedData?.model}
                      />
                    </InfoContainer>
                    <InfoContainer>
                      <Label>Product Page</Label>
                      {matchedData?.productPage ? (
                        <LinkText
                          href={matchedData.productPage}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {`${matchedData.productPage} Page`}
                        </LinkText>
                      ) : (
                        <EmptyText>Empty</EmptyText>
                      )}
                    </InfoContainer>
                    <InfoContainer>
                      <Label>Product Lifecycle</Label>
                      <ProductLifeCycleComponent
                        status={matchedData?.productStatus}
                      />
                    </InfoContainer>
                  </Grid>
                </Row>
                <Bottom>
                  <DescriptionSection>
                    <Label>Description</Label>
                    {matchedData?.description ? (
                      <DescriptionText
                        className="bscroll"
                        dangerouslySetInnerHTML={{
                          __html: matchedData?.description,
                        }}
                      />
                    ) : (
                      <EmptyDescriptionText>Empty</EmptyDescriptionText>
                    )}
                  </DescriptionSection>
                  <SearchSection>
                    <Label>Product Specs</Label>
                    <ProductSpecContainer className="bscroll">
                      <ProductSpecsBody productId={selectedProductId.get()} />
                    </ProductSpecContainer>
                  </SearchSection>
                </Bottom>
              </>
            )}

            {isError && !isLoading && (
              <EmptyProductDetails>Error loading product</EmptyProductDetails>
            )}
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
